import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import { jwtDecode } from "jwt-decode";
import axios from 'axios';

const SocialCallbackHandler = ({ onLogin }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [status, setStatus] = useState('Processing authentication...');

    useEffect(() => {
        // Prevent duplicate processing using a more robust approach
        const urlParams = new URLSearchParams(location.search);
        const code = urlParams.get("code");
        const sessionKey = `oauth_processing_${code}`;
        
        // Check if we've already processed this code
        if (sessionStorage.getItem(sessionKey)) {
            setStatus("Authentication already in progress...");
            return;
        }
        
        // Immediately set the processing flag BEFORE any async operations
        sessionStorage.setItem(sessionKey, 'true');
        
        const handleOAuthCallback = async () => {
            try {
                
                setStatus("Processing Microsoft login...");
                
                // Parse URL parameters
                const urlParams = new URLSearchParams(location.search);
                const code = urlParams.get("code");
                const error = urlParams.get("error");
                const stateParam = urlParams.get('state');
                
                // Handle OAuth errors
                if (error) {
                    setStatus("Login cancelled or failed.");
                    setTimeout(() => navigate("/"), 3000);
                    return;
                }
                
                // Ensure we have an authorization code
                if (!code) {
                    setStatus("No authorization code found. Redirecting...");
                    setTimeout(() => navigate("/"), 3000);
                    return;
                }
                
                // Parse state parameter to determine login type and redirect URL
                let isAdminLogin = false;
                let redirectUrl = "/dashboard";
                
                if (stateParam) {
                    try {
                        const stateData = JSON.parse(decodeURIComponent(stateParam));
                        isAdminLogin = stateData.isAdmin === true;
                        redirectUrl = stateData.returnUrl || "/dashboard";
                    } catch (e) {
                        // Could not parse state parameter
                    }
                }
                
                setStatus("Exchanging authorization code...");
                
                // Exchange authorization code for user data
                const response = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/auth/social/login/`, 
                    {
                        provider: "microsoft",
                        code: code,
                        redirect_uri: `${window.location.origin}/social-callback`,
                    }, 
                    {
                        withCredentials: true
                    }
                );

                const { user } = response.data;
                
                // Admin-specific email authorization check
                if (isAdminLogin) {
                    setStatus("Checking admin authorization...");
                    const authorizedEmails = ['nizankhan@gmail.com', 'nizankhan@hotmail.com'];
                    
                    if (!authorizedEmails.includes(user.email)) {
                        setStatus(`Access denied. Email ${user.email} is not authorized for admin access.`);
                        setTimeout(() => navigate("/"), 3000);
                        return;
                    }
                }
                
                setStatus(`Login successful! Redirecting to ${redirectUrl === "/dashboard" ? "dashboard" : redirectUrl}...`);
                
                // Call onLogin callback if provided
                if (typeof onLogin === "function") {
                    onLogin(user);
                }
                
                // Clean up session key
                sessionStorage.removeItem(sessionKey);
                
                setStatus(`Redirecting to ${redirectUrl === "/dashboard" ? "dashboard" : redirectUrl}... Please wait.`);
                
                // Use window.location.href for reliable redirect (bypasses React Router protection)
                setTimeout(() => {
                    window.location.href = redirectUrl;
                }, 800); // Give more time for state update and cookies to be processed
                
            } catch (error) {
                setStatus("Authentication failed. Please try again.");
                sessionStorage.removeItem(sessionKey); // Clean up on error
                setTimeout(() => navigate("/"), 3000);
            }
        };

        handleOAuthCallback();
    }, [navigate, location, onLogin]);

    return (
        <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            minHeight="100vh"
            gap={2}
        >
            <CircularProgress size={50} />
            <Typography variant="h6" color="primary">
                {status}
            </Typography>
        </Box>
    );
};

export default SocialCallbackHandler;
